// Pure date arithmetic for the DatePicker component.
//
// Deliberately free of Alpine and DOM references so it can be unit-tested with
// `bun test`. Dates are ISO yyyy-mm-dd strings throughout: that format sorts
// lexicographically in chronological order, which is why every comparison here
// is a plain string comparison rather than a Date conversion.

export type PickerMode = "range" | "singleDay";
export type PickStep = "start" | "end";

export interface Bounds {
  minISO: string;
  maxISO: string;
}

export interface DayCell {
  iso: string;
  day: number;
  currentMonth: boolean;
  disabled: boolean;
}

export interface PickState {
  startISO: string;
  endISO: string;
  step: PickStep;
}

export interface PickResult extends PickState {
  /** False when the click was rejected, so callers can skip re-rendering. */
  changed: boolean;
  /** True when the panel should close (single-day selection completed). */
  close: boolean;
}

const MS_PER_DAY = 86400000;

export function toISO(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function fromISO(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export function inBounds(iso: string, b: Bounds): boolean {
  if (b.minISO && iso < b.minISO) return false;
  if (b.maxISO && iso > b.maxISO) return false;
  return true;
}

export function buildCalendarDays(year: number, month: number, b: Bounds): DayCell[] {
  const first = new Date(year, month, 1);
  // Monday-first: JS getDay() is Sunday-based, so rotate by 6 and wrap.
  const firstWeekday = (first.getDay() + 6) % 7;
  const gridStart = new Date(year, month, 1 - firstWeekday);

  const days: DayCell[] = [];
  for (let i = 0; i < 42; i++) {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + i);
    const iso = toISO(date);
    days.push({
      iso,
      day: date.getDate(),
      currentMonth: date.getMonth() === month,
      disabled: !inBounds(iso, b),
    });
  }
  return days;
}

export function canGoPrevMonth(year: number, month: number, b: Bounds): boolean {
  if (!b.minISO) return true;
  // Day 0 of the current month is the last day of the previous month.
  return toISO(new Date(year, month, 0)) >= b.minISO;
}

export function canGoNextMonth(year: number, month: number, b: Bounds): boolean {
  if (!b.maxISO) return true;
  return toISO(new Date(year, month + 1, 1)) <= b.maxISO;
}

export function applyPick(
  state: PickState,
  iso: string,
  mode: PickerMode,
  b: Bounds,
): PickResult {
  if (!inBounds(iso, b)) {
    return { ...state, changed: false, close: false };
  }

  if (mode === "singleDay") {
    return { startISO: iso, endISO: "", step: "start", changed: true, close: true };
  }

  if (state.step === "start") {
    // A start after the current end would invert the range, so drop the end.
    const endISO = state.endISO && iso > state.endISO ? "" : state.endISO;
    return { startISO: iso, endISO, step: "end", changed: true, close: false };
  }

  // step === "end". Picking the end completes the range, so the panel closes
  // — unless no start exists yet (the split-box trigger can open the end half
  // first), in which case there is still something to pick.
  //
  // Clicking before the start swaps the two bounds. This can
  // never produce an out-of-bounds start: the only value assigned to startISO
  // is `iso`, which the guard above already proved in bounds. A stored past
  // start (below min) is therefore left alone — `iso >= min > startISO` means
  // the swap branch is not taken.
  if (state.startISO && iso < state.startISO) {
    return { startISO: iso, endISO: state.startISO, step: "end", changed: true, close: true };
  }
  return { ...state, endISO: iso, changed: true, close: state.startISO !== "" };
}

export function countDays(startISO: string, endISO: string): number {
  if (!startISO || !endISO) return 0;
  const s = fromISO(startISO).getTime();
  const e = fromISO(endISO).getTime();
  if (e < s) return 0;
  return Math.round((e - s) / MS_PER_DAY) + 1;
}

export function countNights(startISO: string, endISO: string): number {
  return Math.max(0, countDays(startISO, endISO) - 1);
}

/**
 * Estimated height in px of the open calendar panel: month header (~64), weekday
 * row (~32), six 36px week rows with padding (~240) and, in range mode, the
 * step header (~41). Estimated rather than measured: a panel hidden by x-show
 * has no layout box the first time it opens.
 */
export const PANEL_HEIGHT_ESTIMATE = 400;

/** Gap kept between the panel and the viewport edge (and the trigger offset). */
export const PANEL_VIEWPORT_MARGIN = 8;

/**
 * True when the panel should open above the trigger: there is not enough room
 * below it AND there is more room above than below. When neither side fits, the
 * larger side wins. A non-finite viewport height never flips.
 */
export function shouldPlaceAbove(
  triggerTop: number,
  triggerBottom: number,
  viewportHeight: number,
  panelHeight: number = PANEL_HEIGHT_ESTIMATE,
): boolean {
  const spaceBelow = viewportHeight - triggerBottom;
  const spaceAbove = triggerTop;
  return (
    spaceBelow < panelHeight + PANEL_VIEWPORT_MARGIN && spaceAbove > spaceBelow
  );
}
